
import React, { useEffect, useMemo, useState } from 'react'
import { Card, Col, Nav, Row, Tab } from 'react-bootstrap'
import moment from 'moment'
import { useDispatch } from 'react-redux'

import config from '@/config'
import DateFunctions from '@/Helpers/DateFunctions'
import { Can } from '@casl/react'

import { useAuth } from '@/Hooks/UseAuth'
import { useDatePicker } from '@/Hooks/UseDatePicker'
import { useProject } from '@/Hooks/UseProject'
import useTranslation from '@/Hooks/UseTranslation'

import InterfaceActions from '@/Redux/Actions/InterfaceActions'
import ProjectsActions from '@/Redux/Actions/ProjectsActions'

import ProjectLayout from '@/Layouts/MainLayout/ProjectLayout'
import ContentBody from '@/Layouts/MainLayout/components/ContentBody'
import ContentHeader from '@/Layouts/MainLayout/components/ContentHeader'

import Toast from '@/Components/Toast/Toast'
import InputFormControl from '@/Components/InputFormControl/InputFormControl'
import InputMaskedControl from '@/Components/InputMaskedControl/InputMaskedControl'
import ClientLogoUploader from '@/Components/ClientLogoUploader/ClientLogoUploader'
import SelectFormControl from '@/Components/SelectFormControl/SelectFormControl'
import InputText from '@/Components/ProgrammationManager/components/InputText'
import NonWorkingDay from './Components/NonWorkingDay/NonWorkingDay'

const ProjectInfoPage = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { ability } = useAuth()

  const { project, nonWorkingDays: rawNonWorkingDays } = useProject()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [tab, setTab] = useState('PROJECT')
  const [editable, setEditable] = useState(false)
  const [logoUploaded, setLogoUploaded] = useState<any>({});

  const [state, setState] = useState({
    project: { ...project },
    editingProject: false,
    editingClient: false,
    editingSupervisor: false,
    editingSettings: false,
    projectLoaded: false
  })

  const [ nonWorkingDays, setNonWorkingDays ] = useState<string[]>([])

  const changeTab = (tab: string) => {

    if (!editable) {

      setTab(tab)
    }
  }

  const handleProject = (project: any) => {

    setState({
      ...state,
      project
    })
  }

  const handleClient = (client: any) => {

    const projectModified = { ...state.project }
    projectModified.client = client

    setState({
      ...state,
      project: projectModified
    })
  }

  const handleSupervisor = (supervisor: any) => {

    const project = { ...state.project }
    project.supervisor = supervisor

    setState({
      ...state,
      project
    })
  }

  const handleMethod = (method: any) => {

      const settings = { ...state.project.settings }
      settings.method = method

      const project = {
          ...state.project,
          settings
      }

      setState({
          ...state,
          project
      })
  }

  const handleLookahead = (lookahead: any) => {

      const settings = { ...state.project.settings }
      settings.lookahead = lookahead

      const project = {
          ...state.project,
          settings
      }

      setState({
          ...state,
          project
      })
  }

  const toggleWorkDay = (day: string) => {

      const project = { ...state.project }

      project.settings[ day ] = !project.settings[ day ]

      setState({
          ...state,
          project
      })
  }

  const handleWorktime = (key: string, value: any) => {

      const project = { ...state.project }

      if (key === 'start') {

          project.settings.workTimeStart = value
      } else if (key === 'end') {

          project.settings.workTimeEnd = value
      }

      setState({
          ...state,
          project
      })
  }

  const handleWorkTimeAtWeek = (value: string) => {

      const project = { ...state.project }

      project.settings.workTimeAtWeek = value

      setState({
          ...state,
          project
      })
  }

  const resetProjectCard = () => {

    const {
      name,
      shortName,
      address,
      cost,
      startsAt,
      endsAt,
      code,
    } = project

    const reseted = {
      ...state.project,
      name,
      shortName,
      address,
      cost,
      startsAt,
      endsAt,
      code,
    }

    setState({
      ...state,
      project: reseted,
      editingProject: false,
    })
  }

  const resetClientCard = () => {

    const {
      name,
      address,
      email,
      phone,
    } = project.client

    const resetedClient = {
      ...state.project.client,
      name,
      address,
      email,
      phone,
    }

    const resetedProject = {
      ...state.project,
      client: resetedClient,
    }

    setState({
      ...state,
      project: resetedProject,
      editingClient: false,
    })
  }

  const resetSupervisorCard = () => {

    const {
      name,
      address,
      email,
      phone,
    } = project.supervisor

    const resetedSupervisor = {
      ...state.project.supervisor,
      name,
      address,
      email,
      phone,
    }

    const resetedProject = {
      ...state.project,
      supervisor: resetedSupervisor,
    }

    setState({
      ...state,
      project: resetedProject,
      editingSupervisor: false,
    })
  }

  const resetSettingsCard = () => {

    const {
      method,
      lookahead,
      workTimeStart,
      workTimeEnd,
      workTimeAtWeek,
      workMonday,
      workTuesday,
      workWednesday,
      workThursday,
      workFriday,
      workSaturday,
      workSunday,
    } = project.settings

    const resetedSettings = {
      ...state.project.settings,
      method,
      lookahead,
      workTimeStart,
      workTimeEnd,
      workTimeAtWeek,
      workMonday,
      workTuesday,
      workWednesday,
      workThursday,
      workFriday,
      workSaturday,
      workSunday,
    }

    const resetedProject = {
      ...state.project,
      settings: resetedSettings,
    }

    setState({
      ...state,
      project: resetedProject,
      editingSettings: false,
    })
  }

  const unSetEditable = () => {

    switch (tab) {
      case 'PROJECT':
        resetProjectCard()
        break
      case 'CLIENT':
        resetClientCard()
        break
      case 'SUPERVISOR':
        resetSupervisorCard()
        break
      case 'SETTINGS':
        resetSettingsCard()
        break
    }

    setEditable(false)
  }

  const daysEnabled = () => {

    const {
      settings,
    } = state.project

    let days = 0

    days += settings.workMonday ? 1 : 0
    days += settings.workTuesday ? 1 : 0
    days += settings.workWednesday ? 1 : 0
    days += settings.workThursday ? 1 : 0
    days += settings.workFriday ? 1 : 0
    days += settings.workSaturday ? 1 : 0
    days += settings.workSunday ? 1 : 0

    return days
  }

  const checkNonWorkingDays = () => {

    let valid = true

    nonWorkingDays.map((day: string) => {

      if (day === 'Fecha inválida' || day === '') {

        valid = false
      }
    })

    return valid
  }

  const checkTimeExtensions = () => {

    let valid = true

    state.project.timeExtensions.map((days: number) => {

      if ((days * 1) === 0) {

        valid = false
      }
    })

    return valid
  }

  const addNonWorkingDay = () => {

    const nwd = [...nonWorkingDays, '']

    setNonWorkingDays(nwd)
  }

  const removeNonWorkingDay = (index: number) => {

    // remove the day from the array nonWorkingDays
    const newNonWorkingDays = nonWorkingDays.filter((day: string, i: number) => i !== index)

    setNonWorkingDays(newNonWorkingDays)
  }

  const updateNonWorkingDay = (index: number, value: any) => {

    // update the day in the array nonWorkingDays
    const newNonWorkingDays = nonWorkingDays.map((day: string, i: number) => {

      return i === index ? value : day
    })

    setNonWorkingDays(newNonWorkingDays)
  }

  const saveProject = async () => {

    if (tab === 'SETTINGS') {

      if (daysEnabled() === 0) {

        alert(t('PROJECT_INFO:SaveErrorNoDaysSelected'))
        return
      }

      if (!checkNonWorkingDays()) {

        alert(t('PROJECT_INFO:SaveErrorInvalidNWD'))
        return
      }

      if (!checkTimeExtensions()) {

        alert(t('PROJECT_INFO:SaveErrorInvalidTimeExtensions'))
        return
      }
    }

    dispatch(InterfaceActions.showLoader())

    const data = {
      ...state.project,
      nonWorkingDays,
    }

    const res: any = await dispatch(ProjectsActions.updateAction(data))

    const [error] = res

    dispatch(InterfaceActions.hideLoader())

    if (error) {

      let err_msg = t('PROJECT_INFO:SaveErrorUnknownError')

      if (error.response.data.error === 'invalid_start_date') {
        err_msg = t('PROJECT_INFO:SaveErrorInvalidStartDate')
      }

      if (error.response.data.error === 'invalid_end_date') {
        err_msg = t('PROJECT_INFO:SaveErrorInvalidEndDate')
      }

      Toast.show(t('PROJECT_INFO:SaveErrorTitle'), err_msg)

      return
    }

    setEditable(false)
  }

  const handleLogoUploaded = (image: any) => {

    console.log('image uploaded', image)

    const client = { ...state.project.client }
    client.logo = `${image.destination}/${image.fileName}`

    const project = { ...state.project }
    project.client = client

    setState({
      ...state,
      project
    })

    setLogoUploaded(image);
  };

  const handleChangeDates = async (dates: any) => {

    console.log('dates', dates)

      const {
          startDate,
          endDate
      } = dates

      setState({
        ...state,
        project: {
          ...state.project,
          startsAt: startDate,
          endsAt: endDate,
        }
      })

      hideDatePicker()
    }

  const handleShowDateRangePicker = () => {

    showDateRangePicker({
      startDate: (startsAt || moment()).format('YYYY-MM-DD'),
      endDate: (endsAt || moment()).format('YYYY-MM-DD'),
      nonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  useEffect(() => {

    if (project._id) {

      setState({
        project: { ...project },
        editingProject: false,
        editingClient: false,
        editingSupervisor: false,
        editingSettings: false,
        projectLoaded: false
      })
    }
  }, [project])

  useEffect(() => {

    if (!project._id) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [project._id])

  useEffect(() => {

    const YMD = rawNonWorkingDays.map((day: string) => moment(day, 'YYYY-MM-DD').format('YYYY-MM-DD'))

    // sort an array of YYYY-MM-DD strings
    const sorted = YMD.sort((a, b) => moment(a, 'YYYY-MM-DD').diff(moment(b, 'YYYY-MM-DD')))

    setNonWorkingDays(sorted)

  }, [ rawNonWorkingDays ])

  console.log('project', state.project)
  console.log('nonWorkingDays', nonWorkingDays)

  const startsAt = moment(state.project.startsAt, 'YYYY-MM-DD')
  const endsAt = moment(state.project.endsAt, 'YYYY-MM-DD')
  // const nonWorkingDays = getNonWorkingDaysFromRange(state.project.startsAt, state.project.endsAt, state.project)
  const diff = DateFunctions.getDiffDays(state.project.startsAt, state.project.endsAt, nonWorkingDays)

  return (
    <ProjectLayout menuName="home" subMenuName="info">
      <ContentHeader title={t('PROJECT_INFO:PageTitle')}>
        <div className='ul-widget__head-toolbar'>
          <div className='text-right'>
            <Can I="update" this="Project" ability={ability}>
              {!editable && (
                <button
                  className="btn btn-link btn-link-header"
                  onClick={() => setEditable(true)}
                >
                  <span className="icon-editar"></span> {t('PROJECT_INFO:ToolbarEditButton')}
                </button>
              )}
              {editable && (
                <>
                  <button
                    className="btn btn-link btn-link-header"
                    onClick={() => unSetEditable()}
                  >
                    <span className="icon-cancelar"></span> {t('PROJECT_INFO:ToolbarCancelButton')}
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-link btn-link-header"
                    onClick={() => saveProject()}
                  >
                    <span className="icon-guardar"></span> {t('PROJECT_INFO:ToolbarSaveButton')}
                  </button>
                </>
              )}
            </Can>
          </div>
        </div>
        <div className="ul-widget__head-nav">
          <Tab.Container>
            <Nav as="ul" className="nav-tabs nav-tabs-line nav-tabs-bold">
              <Nav.Item as="li">
                <Nav.Link active={tab === 'PROJECT'} onClick={() => changeTab('PROJECT')}>
                  <span className="icon-datos_proyecto"></span>
                  <span className="hide-on-mobile"> {t('PROJECT_INFO:ToolbarProjectTab')}</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link active={tab === 'CLIENT'} onClick={() => changeTab('CLIENT')}>
                  <span className="icon-cliente"></span>
                  <span className="hide-on-mobile"> {t('PROJECT_INFO:ToolbarClientTab')}</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link active={tab === 'SUPERVISOR'} onClick={() => changeTab('SUPERVISOR')}>
                  <span className="icon-supervision"></span>
                  <span className="hide-on-mobile"> {t('PROJECT_INFO:ToolbarSupervisorTab')}</span>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item as="li">
                <Nav.Link active={tab === 'SETTINGS'} onClick={() => changeTab('SETTINGS')}>
                  <span className="icon-configuracion"></span>
                  <span className="hide-on-mobile"> {t('PROJECT_INFO:ToolbarSettingsTab')}</span>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Tab.Container>
        </div>
      </ContentHeader>
      <ContentBody>
        <Tab.Content className='no-padding'>
          <Tab.Pane active={tab === 'PROJECT'}>
            <Row>
              <Col md={8} className='mb-3'>
                <Card className='table-card'>
                  <Card.Body>
                    <Row>
                      <Col md={8} className="form-group mb-3">
                        <label htmlFor="large-name">
                          {t('PROJECT_INFO:FullNameInputLabel')}
                        </label>
                        <InputFormControl
                          id="large-name"
                          placeholder={t('PROJECT_INFO:FullNameInputPlaceholder')}
                          value={state.project.name}
                          onChange={(value: string) => handleProject({ ...state.project, name: value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="short-name">
                          {t('PROJECT_INFO:ShortNameInputLabel')}
                        </label>
                        <InputFormControl
                          id="short-name"
                          placeholder={t('PROJECT_INFO:ShortNameInputPlaceholder')}
                          value={state.project.shortName}
                          onChange={(value: string) => handleProject({ ...state.project, shortName: value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col md={8} className="form-group mb-3">
                        <label htmlFor="direction">
                          {t('PROJECT_INFO:AddressInputLabel')}
                        </label>
                        <InputFormControl
                          id="direction"
                          placeholder={t('PROJECT_INFO:AddressInputPlaceholder')}
                          value={state.project.address}
                          onChange={(value: string) => handleProject({ ...state.project, address: value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="cost">
                          {t('PROJECT_INFO:CostInputLabel')}
                        </label>
                        <InputMaskedControl
                          id="cost"
                          placeholder={t('PROJECT_INFO:CostInputPlaceholder')}
                          value={state.project.cost}
                          onChange={(e: any) => handleProject({ ...state.project, cost: e.target.value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="startsAt">
                          {t('PROJECT_INFO:StartDateInputLabel')}
                        </label>
                        <InputText
                          value={startsAt && moment(startsAt).format('DD/MM/YYYY')}
                          placeholder="dd/mm/yyyy"
                          onClick={handleShowDateRangePicker}
                          asButton
                          disabled={!editable}
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="fin">
                          {t('PROJECT_INFO:EndDateInputLabel')}
                        </label>
                        <InputText
                          value={endsAt && moment(endsAt).format('DD/MM/YYYY')}
                          placeholder="dd/mm/yyyy"
                          onClick={handleShowDateRangePicker}
                          asButton
                          disabled={!editable}
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="plazo">
                          {t('PROJECT_INFO:DeadlineInputLabel')}
                        </label>
                        <InputFormControl
                          id="plazo"
                          value={`${diff} días`}
                          onChange={(value: string) => null}
                          readOnly
                        />
                      </Col>
                      <Col md={4} className="form-group mb-3">
                        <label htmlFor="plazo">
                          {t('PROJECT_INFO:CodeInputLabel')}
                        </label>
                        <InputFormControl
                          id="code"
                          value={state.project.code}
                          onChange={(value: string) => handleProject({ ...state.project, code: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane active={tab === 'CLIENT'}>
            <Row>
              <Col md={3} className='mb-3'>
                <Card style={{ minHeight: '100%' }}>
                  <Card.Body>
                    <div className='row'>
                      <div className="col form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:ClientLogo')}
                        </label>
                        <ClientLogoUploader
                          baseUrl={config.apiHost}
                          endpoint={`/upload/image`}
                          image={state.project.client.logo}
                          onUpload={(image: string) => handleLogoUploaded(image)}
                          disabled={!editable}
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:ClientNameLabel')}
                        </label>
                        <InputFormControl
                          value={state.project.client.name || ''}
                          onChange={(value: string) => handleClient({ ...state.project.client, name: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:ClientAddressLabel')}
                        </label>
                        <InputFormControl
                          value={state.project.client.address}
                          onChange={(value: string) => handleClient({ ...state.project.client, address: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={8} md={12} sm={8} className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:ClientMailLabel')}
                        </label>
                        <InputFormControl
                          type="email"
                          value={state.project.client.email}
                          onChange={(value: string) => handleClient({ ...state.project.client, email: value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col lg={4} md={12} sm={4} className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:ClientPhoneLabel')}
                        </label>
                        <InputFormControl
                          type="tel"
                          value={state.project.client.phone}
                          onChange={(value: string) => handleClient({ ...state.project.client, phone: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane active={tab === 'SUPERVISOR'}>
            <Row>
              <Col md={6} className='mb-3'>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:SupervisorNameLabel')}
                        </label>
                        <InputFormControl
                          value={state.project.supervisor.name}
                          onChange={(value: string) => handleSupervisor({ ...state.project.supervisor, name: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:SupervisorAddressLabel')}
                        </label>
                        <InputFormControl
                          value={state.project.supervisor.address}
                          onChange={(value: string) => handleSupervisor({ ...state.project.supervisor, address: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={8} md={12} sm={8} className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:SupervisorMailLabel')}
                        </label>
                        <InputFormControl
                          type="email"
                          value={state.project.supervisor.email}
                          onChange={(value: string) => handleSupervisor({ ...state.project.supervisor, email: value })}
                          readOnly={!editable}
                        />
                      </Col>
                      <Col lg={4} md={12} sm={4} className="form-group mb-3">
                        <label>
                          {t('PROJECT_INFO:SupervisorPhoneLabel')}
                        </label>
                        <InputFormControl
                          type="tel"
                          value={state.project.supervisor.phone}
                          onChange={(value: string) => handleSupervisor({ ...state.project.supervisor, phone: value })}
                          readOnly={!editable}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane active={tab === 'SETTINGS'}>
            <Row className="justify-content-md-center">
              <Col md={8} lg={4} xl={3} className='mb-3'>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col className="form-group mb-3">
                        <label htmlFor="method">
                          {t('PROJECT_INFO:SettingsMethodLabel')}
                        </label>
                        {!state.project.settings ? null : (
                          <SelectFormControl
                            id="method"
                            value={state.project.settings.method}
                            onChange={(value: string) => handleMethod(value)}
                            disabled={!editable}
                          >
                            <option value="Gantt">Gantt</option>
                          </SelectFormControl>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="form-group mb-3">
                        <label htmlFor="method">
                          {t('PROJECT_INFO:SettingsPeriodLabel')}
                        </label>
                        {!state.project.settings ? null : (
                          <SelectFormControl
                            id="method"
                            value={state.project.settings.lookahead}
                            onChange={(value: string) => handleLookahead(value)}
                            disabled={!editable}
                          >
                            <option value="3">3 {t('PROJECT_INFO:SettingsPeriodWeeks')}</option>
                            <option value="4">4 {t('PROJECT_INFO:SettingsPeriodWeeks')}</option>
                            <option value="5">5 {t('PROJECT_INFO:SettingsPeriodWeeks')}</option>
                            <option value="6">6 {t('PROJECT_INFO:SettingsPeriodWeeks')}</option>
                          </SelectFormControl>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col className="form-group mb-3">
                        <label htmlFor="">
                          {t('PROJECT_INFO:SettingsWorkDaysLabel')}
                        </label>
                        <Row>
                          {!state.project.settings ? null : (
                            <div className="col-12">
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workMonday}
                                  onChange={() => toggleWorkDay('workMonday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsMondayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workTuesday}
                                  onChange={() => toggleWorkDay('workTuesday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsTuesdayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workWednesday}
                                  onChange={() => toggleWorkDay('workWednesday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsWednesdayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workThursday}
                                  onChange={() => toggleWorkDay('workThursday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsthursdayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workFriday}
                                  onChange={() => toggleWorkDay('workFriday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsFridayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workSaturday}
                                  onChange={() => toggleWorkDay('workSaturday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsSaturdayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                              <label className="checkbox checkbox-primary">
                                <input type="checkbox"
                                  checked={state.project.settings.workSunday}
                                  onChange={() => toggleWorkDay('workSunday')}
                                  disabled={!editable || state.project.hasTasks}
                                />
                                <span>
                                  {t('PROJECT_INFO:SettingsSundayOption')}
                                </span>
                                <span className="checkmark"></span>
                              </label>
                            </div>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} lg={4} xl={3} className='mb-3'>
                <Card className='mb-3'>
                  <Card.Body>
                    <Row>
                      <Col className="form-group mb-3">
                        <label htmlFor="">
                          {t('PROJECT_INFO:SettingsOvertimeLabel')}
                        </label>
                        <Row>
                          {!state.project.overtime ? null : (
                            <>
                              {state.project.overtime.length ? null : (
                                <>
                                  <Col className="mb-2">
                                    <InputFormControl
                                      type="text"
                                      value={t('PROJECT_INFO:SettingsNoOvertime')}
                                      onChange={() => null}
                                      readOnly
                                    />
                                  </Col>
                                </>
                              )}
                              {state.project.overtime.map((_time: any, i: number) => (
                                <>
                                  <Col className="col-md-4 col-lg-3 mb-2">
                                    <InputFormControl
                                      type="time"
                                      placeholder={t('PROJECT_INFO:SettingsOvertimeStartPlaceholder')}
                                      value={state.project.settings.workTimeStart}
                                      onChange={() => null}
                                      readOnly
                                    />
                                  </Col>
                                  <Col className="col-md-4 col-lg-3 mb-2">
                                    <InputFormControl
                                      type="time"
                                      placeholder={t('PROJECT_INFO:SettingsOvertimeEndPlaceholder')}
                                      value={state.project.settings.workTimeEnd}
                                      onChange={() => null}
                                      readOnly
                                    />
                                  </Col>
                                  <Col className="col-md-4 col-lg-3 mb-2">
                                    <InputFormControl
                                      type="number"
                                      placeholder={t('PROJECT_INFO:SettingsOvertimeTotalPlaceholder')}
                                      value={state.project.settings.workTimeAtWeek}
                                      onChange={() => null}
                                      readOnly
                                    />
                                  </Col>
                                </>
                              ))}
                              {!project.isMine ? null : (
                                <Col className="mb-3">
                                  <button className="btn btn-secondary"
                                    disabled={!editable}>
                                    <span className="icon-agregar"></span> {t('PROJECT_INFO:SettingsOvertimeAddButton')}
                                  </button>
                                </Col>
                              )}
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Body>
                    <Row>
                      <Col className="form-group mb-3">
                        <label htmlFor="">
                          {t('PROJECT_INFO:NonWorkingDaysLabel')}
                        </label>
                        {(!nonWorkingDays || nonWorkingDays.length === 0) && (
                          <Row>
                            <Col className="mb-2">
                              <InputFormControl
                                type="text"
                                value={t('PROJECT_INFO:NoNonWorkingDays')}
                                onChange={() => null}
                                readOnly
                              />
                            </Col>
                          </Row>
                        )}
                        {!nonWorkingDays ? null : (
                          <>
                            {nonWorkingDays.map((day: string, i: number) => (
                              <Row key={i}>
                                <Col className="mb-2">
                                  <NonWorkingDay
                                    value={day}
                                    onChange={(value: any) => updateNonWorkingDay(i, value)}
                                    onRemove={() => removeNonWorkingDay(i)}
                                    disabled={!editable}
                                  />
                                </Col>
                              </Row>
                            ))}
                            {!project.isMine ? null : (
                              <Row>
                                <Col className="mb-3">
                                  <button
                                    className="btn btn-secondary"
                                    onClick={() => addNonWorkingDay()}
                                    disabled={!editable}
                                  >
                                    <span className="icon-agregar"></span> {t('PROJECT_INFO:NonWorkingDaysAddButton')}
                                  </button>
                                </Col>
                              </Row>
                            )}
                          </>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </ContentBody>
    </ProjectLayout>
  )
}

export default ProjectInfoPage
